import type { FieldStartBlocker, OperatorRoutePlan } from '@groupe-reca/contracts'

export type PlanRoute = OperatorRoutePlan['routes'][number]

/** Seules ces actions mènent à un départ; IN_USE et COMPLETED se lisent, ne se choisissent pas. */
export function canStartRoute(route: PlanRoute): boolean {
  return route.available && (route.action === 'START' || route.action === 'RESUME' || route.action === 'NEW_PASS')
}

/**
 * Route du prochain départ : celle que l'opérateur a touchée si elle reste admissible dans le plan relu, sinon la
 * route suggérée par le serveur (`nextRouteId`). Jamais un démarrage automatique : l'opérateur confirme toujours.
 */
export function chosenRoute(plan: OperatorRoutePlan | undefined, picked: string | null): PlanRoute | null {
  if (!plan) return null
  const pickedRoute = plan.routes.find((route) => route.routeId === picked)
  if (pickedRoute && canStartRoute(pickedRoute)) return pickedRoute
  return plan.routes.find((route) => route.routeId === plan.nextRouteId) ?? null
}

/** Les blocages de route de l'accueil portent sur la route principale : avec un plan, la route choisie décide. */
const routeBlockers: ReadonlySet<FieldStartBlocker> = new Set(['ROUTE_MISSING', 'ROUTE_UNAVAILABLE', 'ROUTE_EMPTY', 'ROUTE_IN_USE'])

export function departureBlockers(blockers: readonly FieldStartBlocker[], plan: OperatorRoutePlan | undefined): FieldStartBlocker[] {
  if (!plan || plan.routes.length === 0) return [...blockers]
  return blockers.filter((blocker) => !routeBlockers.has(blocker))
}

export function routeActionLabel(route: PlanRoute): string {
  switch (route.action) {
    case 'START':
      return route.passNumber > 1 ? `Démarrer · passage ${String(route.passNumber)}` : 'Démarrer'
    case 'RESUME':
      return `Reprendre · passage ${String(route.passNumber)}`
    case 'NEW_PASS':
      return `Nouveau passage ${String(route.passNumber)}`
    case 'IN_USE':
      return route.inUseBy ? `En cours · ${route.inUseBy}` : 'En cours par un autre opérateur'
    case 'COMPLETED':
      return 'Terminée dans cette opération'
  }
}

/** Résidences du prochain départ : restantes pour une reprise, admissibles pour un départ ou un nouveau passage. */
export function routeResidences(route: PlanRoute): string {
  if (route.action === 'RESUME') return `${String(route.remaining)} restante${route.remaining > 1 ? 's' : ''}`
  if (route.action === 'START' || route.action === 'NEW_PASS') return `${String(route.eligibleNow)} à faire`
  if (route.total > 0) return `${String(route.done)}/${String(route.total)} réglées`
  return '—'
}
